import { Component, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Json } from '@se/cp.common';
import { NotificationService } from '../../notification.service';
import { fillTemplateWithData, jsonToIntegrationStoreData } from '../routines';
import { TpIntegrationsCacheService } from '../tp-integrations-cache.service';
import {
    EExternalIntegrationCallType,
    EOnError,
    EOperator,
    FullIntegrationStoreData,
    IExternalIntegration,
    IExternalIntegrationCall,
    INotificationMessage,
} from '../tp-integrations.interface';
import { ArrayFindCallComponent } from './array-find-call/array-find-call.component';
import { ErrorCallComponent } from './error-call/error-call.component';
import { FormCallComponent } from './form-call/form-call.component';
import { IframeCallComponent } from './iframe-call/iframe-call.component';
import { NotificationCallComponent } from './notification-call/notification-call.component';
import { RestCallComponent } from './rest-call/rest-call.component';
import { TabWindowCallComponentComponent } from './tab-window-call-component/tab-window-call-component.component';

export interface IDialogData {
    id: string;
    data: FullIntegrationStoreData;
    externalIntegrationData: IExternalIntegration;
    onNotify: (message: INotificationMessage) => void;
}

@Component({
    selector: 'se-stack-manager',
    templateUrl: './stack-manager.component.html',
    styleUrls: ['./call-style/call.scss'],
    imports: [
        MatDialogModule,
        MatButtonModule,
        IframeCallComponent,
        FormCallComponent,
        NotificationCallComponent,
        TabWindowCallComponentComponent,
        RestCallComponent,
        ArrayFindCallComponent,
        ErrorCallComponent,
    ],
    standalone: true,
})
export class StackManagerComponent implements OnInit {
    private readonly notificationService = inject(NotificationService);
    private readonly translate = inject(TranslateService);
    private readonly cache = inject(TpIntegrationsCacheService);
    private readonly dialogRef = inject(MatDialogRef<StackManagerComponent>);
    private readonly data: IDialogData = inject(MAT_DIALOG_DATA);

    onNotify = this.data.onNotify;
    dataStore: FullIntegrationStoreData | null = null;
    id: string = '';
    calls: IExternalIntegrationCall[] = [];
    debug: boolean = false;
    currentIndex: number = 0;
    currentCall: IExternalIntegrationCall | null = null;
    awaitConfirm: boolean = false; // usato quando tra una call e l'altra vogliamo dare un messaggio all'utente ed attendere che legga
    confirmButtonLabel: string = '';
    confirmText: string = '';

    ngOnInit(): void {
        this.id = this.data.id;
        this.dataStore = this.data.data;
        this.debug = this.data.externalIntegrationData.debug;
        this.calls = this.data.externalIntegrationData.calls;
        this.setCall();
    }

    setCall = () => {
        const currentCall = this.calls[this.currentIndex];
        if (currentCall.cacheKey && currentCall.target && this.dataStore) {
            currentCall.cacheKey = fillTemplateWithData(this.dataStore, currentCall.placeholders, currentCall.cacheKey);
            const cachedData = this.cache.get(currentCall.cacheKey);
            if (cachedData) {
                this.dataStore[currentCall.target] = cachedData as Json;
                this.dataStore = jsonToIntegrationStoreData(cachedData as Json, currentCall.target, this.dataStore);

                return this.next();
            }
        }

        if (currentCall.type === EExternalIntegrationCallType.CACHE_OP) {
            console.log('CACHE_OP', this.dataStore, currentCall);
            return this.next();
        }
        this.currentCall = currentCall;
    };

    confirmButtonClick = () => {
        this.awaitConfirm = false;
        this.confirmText = '';
        this.currentIndex++;
        this.setCall();
    };

    next = () => {
        /* if current call is blocked by notification then add a notification within the template of stack-manager*/
        if (this.currentCall?.notifySuccessString || this.currentCall?.notifySuccessPath) {
            this.confirmButtonLabel = this.translate.instant('OK');
            this.currentCall.notifySuccessPath;
            this.confirmText =
                this.currentCall.notifySuccessString || `${this.dataStore?.[this.currentCall.notifySuccessPath ?? '']}`;
            this.awaitConfirm = true;
            return;
        }
        this.currentIndex++;
        this.setCall();
    };

    complete = () => {
        this.dataStore = null;
        this.id = '';
        this.calls = [];
        this.currentIndex = 0;
        this.currentCall = null;
        this.dialogRef.close();
    };

    completeWithError = (message: string = 'unrecognized call type') => {
        this.currentIndex = 0;
        this.currentCall = {
            description: 'Error',
            type: EExternalIntegrationCallType.ERROR,
            placeholders: [],
            errorHandling: {
                action: EOnError.BREAK,
                message,
                path: '',
                operator: EOperator.EQUAL,
                valueTrigger: '',
            },
        };
        this.calls = [this.currentCall];
    };

    onCallEnd = (newData: FullIntegrationStoreData) => {
        // store data
        this.dataStore = {
            ...this.dataStore,
            ...newData,
        };

        /* cache management*/
        if (this.currentCall?.cacheKey && this.currentCall.target) {
            const originalResponse = newData[this.currentCall.target];
            this.cache.set(this.currentCall.cacheKey, originalResponse);
        }

        if (this.debug) {
            console.log(`INTEGRATIONS: onCallEnd`, {
                dataStore: this.dataStore,
                newData,
                currentIndex: this.currentIndex,
            });
        }

        //next or complete
        if (this.calls.length - 1 === this.currentIndex) this.complete();
        else this.next();
    };

    onCallError = (errorMessage: string) => {
        if (this.debug) {
            console.log(`INTEGRATIONS: onCallError`, {
                errorMessage,
                currentCall: this.currentCall,
            });
        }

        if (errorMessage === 'FORCE_CLOSE') {
            return this.complete();
        }

        const currentCall = this.currentCall;
        const errorHandling = currentCall?.errorHandling;
        const message =
            errorMessage && errorHandling?.message && errorMessage !== errorHandling?.message
                ? `${errorMessage} - ${errorHandling?.message}`
                : `${errorHandling?.message || errorMessage || 'GenericIntegrationError'}`;

        if (errorHandling?.action === EOnError.CONTINUE) {
            this.notificationService.warning(message);
            this.next();
        } /*if (errorAction === EOnError.BREAK) per ora di default facciamo break*/ else {
            this.notificationService.error(message);
            if (message) this.completeWithError(message);
            else this.complete();
        }
    };

    showModalWindowUrlCall = (call: IExternalIntegrationCall, index: number) =>
        call.type === EExternalIntegrationCallType.MODAL_WINDOW_URL && index === this.currentIndex;

    showTabWindowUrlCall = (call: IExternalIntegrationCall, index: number) =>
        call.type === EExternalIntegrationCallType.TAB_WINDOW_URL && index === this.currentIndex;

    showRestCall = (call: IExternalIntegrationCall, index: number) =>
        (call.type === EExternalIntegrationCallType.REST_EXTERNAL ||
            call.type === EExternalIntegrationCallType.REST_EXTERNAL_SOAP ||
            call.type === EExternalIntegrationCallType.REST_ESTENSA) &&
        index === this.currentIndex;

    showErrorCall = (call: IExternalIntegrationCall, index: number) =>
        !EExternalIntegrationCallType[call.type] || call.type === EExternalIntegrationCallType.ERROR;

    showArrayFindCall = (call: IExternalIntegrationCall, index: number) =>
        call.type === EExternalIntegrationCallType.FIND_IN_DATA && index === this.currentIndex;

    showFormCall = (call: IExternalIntegrationCall, index: number) =>
        call.type === EExternalIntegrationCallType.FORM && index === this.currentIndex;

    showNotificationCall = (call: IExternalIntegrationCall, index: number) =>
        call.type === EExternalIntegrationCallType.NOTIFICATION && index === this.currentIndex;
}
